export type PrescriptionFrequencyShortcut = '4h' | '6h' | '8h' | '12h' | '1d' | 'dor'

const SHORTCUT_DOSES: Record<PrescriptionFrequencyShortcut, number> = {
  '4h': 6,
  '6h': 4,
  '8h': 3,
  '12h': 2,
  '1d': 1,
  dor: 0,
}

const PHRASE_EVERY_HOURS = /de\s+(\d+)\s+em\s+\1/i
const INCLUSION_HYPHENS = '-'.repeat(15)
const MISSING_INPUT_HINT = 'Informe a frequência e os dias para sugerir a quantidade.'

export function dosesPerDay(phrase: string, shortcut: PrescriptionFrequencyShortcut | null): number {
  if (shortcut) return SHORTCUT_DOSES[shortcut]

  const match = phrase.match(PHRASE_EVERY_HOURS)
  if (!match) return 0

  const hours = Number(match[1])
  if (!Number.isFinite(hours) || hours <= 0 || 24 % hours !== 0) return 0
  return 24 / hours
}

export type PrescriptionPeriodUnit = 'dias' | 'meses' | 'continuo'

const CONTINUOUS_CAPSULES = 90

export function durationInDays(amount: number | null, unit: PrescriptionPeriodUnit | null): number {
  if (unit === 'continuo') return 0
  if (amount == null || !Number.isFinite(amount) || amount <= 0 || unit == null) return 0
  if (unit === 'meses') return amount * 30
  return amount
}

export function formatInclusionLine(name: string, capsules: number): string {
  const trimmed = name.trim()
  if (capsules < 1) return trimmed
  return `${trimmed} ${INCLUSION_HYPHENS} ${capsules} cps`
}

export function suggestPrescriptionCapsules(input: {
  name: string
  phrase: string
  shortcut: PrescriptionFrequencyShortcut | null
  amount: number | null
  unit: PrescriptionPeriodUnit | null
}): { capsules: number; hint: string; line: string | null } {
  if (input.unit === 'continuo') {
    return {
      capsules: CONTINUOUS_CAPSULES,
      hint: 'Uso contínuo: sugestão de 90 cápsulas (cerca de 3 meses).',
      line: formatInclusionLine(input.name, CONTINUOUS_CAPSULES),
    }
  }
  const doses = dosesPerDay(input.phrase, input.shortcut)
  const days = durationInDays(input.amount, input.unit)
  if (doses === 0 || days === 0) {
    return { capsules: 0, hint: MISSING_INPUT_HINT, line: null }
  }

  const capsules = doses * days
  const hint =
    input.unit === 'meses'
      ? `${doses} ao dia × ${days} dias (${input.amount} meses) = ${capsules} cápsulas.`
      : `${doses} ao dia × ${days} dias = ${capsules} cápsulas.`

  return {
    capsules,
    hint,
    line: formatInclusionLine(input.name, capsules),
  }
}
